import {
  Component,
  OnInit,
  ViewEncapsulation,
  Injector,
  OnDestroy
} from '@angular/core';
import { ActivatedRoute, Route, Router } from '@angular/router';
import { Observable, BehaviorSubject, of, Subscription, Subject } from 'rxjs';
import { flatMap, map, find, tap, takeUntil, take } from 'rxjs/operators';

import { LocaleUtils } from 'localeselect/locale';

import { AuthenticationService } from '../../authentication/authentication.service';
import { DeviceManagementService } from '../../device-management/device-management.service';
import { EstablishmentService } from '../establishment/establishment.service';
import { NavigationService } from './navigation.service';
import { PermissionService } from '../permission/permission.service';
import { ProductsService } from '../../products/products.service';
import { SideBarService } from '../sidebar/sidebar.service';
import { UsersService } from '../../user-management/users/users.service';
import { FeatureFlagService } from '../feature-flag/feature-flag.service';

import { DynamicRoute } from './dynamic-route';
import { Establishment } from '../establishment/model/establishment';
import { PILL_COLOR_STYLE } from '../components/pill/pill-color-style';
import { ProductLayout } from '../../products/dmo/product-layout';
import { RouteDefinition } from './route-definition';
import { RoutePaths } from './route-paths';
import { RouteType } from './route-type.enum';
import { User } from '../../user-management/users/user';
import { IntercomService } from '../intercom/intercom.service';

@Component({
  selector: 'ls-navigation',
  templateUrl: './navigation.component.html',
  styleUrls: ['./navigation.component.scss'],
  // tslint:disable-next-line:use-view-encapsulation
  encapsulation: ViewEncapsulation.None
})
export class NavigationComponent implements OnInit, OnDestroy {
  public outDatedDevicesPillStyle: string = PILL_COLOR_STYLE.DANGER;
  public locale: string;
  public isSubNavigationOpen$: BehaviorSubject<boolean> = new BehaviorSubject(
    false
  );
  public selectedEstablishment: Establishment;
  public outDatedDevicesCount: number;
  public establishments: Array<Establishment>;
  public routeType: RouteType = RouteType.NORMAL;
  public expandedEstablishmentDropdown = false;
  public displayNameLoggedinUser$: Observable<string>;
  public selectedEstablishment$: Observable<Establishment>;
  public layouts: Array<ProductLayout>;
  public title: string;

  public routes$: Observable<Array<RouteDefinition>>;
  public childRoutes$: Observable<Array<RouteDefinition>>;

  private lastRoute: string = null;
  private isTouchDevice = false;
  private ngUnsubscribe: Subject<Subscription> = new Subject();

  constructor(
    private authenticationService: AuthenticationService,
    private establishmentService: EstablishmentService,
    private deviceService: DeviceManagementService,
    private permission: PermissionService,
    private userService: UsersService,
    private injector: Injector,
    private productApi: ProductsService,
    private sidebarService: SideBarService,
    private navigationService: NavigationService,
    private activatedRoute: ActivatedRoute,
    private intercom: IntercomService,
    private router: Router,
    private featureFlagService: FeatureFlagService
  ) {
    this.locale = LocaleUtils.getLocale();
    this.routeType = this.getRouteTypeFromPermissionLevel();
  }

  public ngOnInit(): void {
    this.isTouchDevice = 'ontouchstart' in window;
    this.init();

    this.establishmentService.establishmentChanged$
      .pipe(
        map((changed: boolean) => {
          if (changed) {
            this.getOutdatedDevices();
          }
        }),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe();

    // take only one otherwise we loose the master information
    this.establishmentService
      .getEstablishments()
      .pipe(
        tap((establishments: Array<Establishment>) => {
          this.establishments = establishments;
          this.selectedEstablishment =
            this.establishmentService.selectedEstablishment ||
            establishments[0];
        }),
        take(1)
      )
      .subscribe();

    this.sidebarService.routeSubject$.subscribe((route: Route) => {
      if (route != null && route.path !== this.lastRoute) {
        this.lastRoute = route.path;
        if (
          route.path === RoutePaths.ACCOUNT ||
          route.path === RoutePaths.NEW_ACCOUNT
        ) {
          this.closeSubNavigation();
        }
        this.childRoutes$ = this.getChildrenOfRoute(<DynamicRoute>route);
      }
    });
  }

  public ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  public resolveRoute(route: RouteDefinition): Observable<boolean> {
    if (route.resolver) {
      // tslint:disable-next-line
      return this.injector.get(route.resolver).resolve();
    } else {
      return of(true);
    }
  }

  public getChildrenOfRoute(
    parentRoute: DynamicRoute
  ): Observable<Array<RouteDefinition>> {
    return this.routes$.pipe(
      flatMap((routeDef: Array<RouteDefinition>) => routeDef),
      find((route: RouteDefinition) => {
        return route.path === parentRoute.path;
      }),
      map((routeDef: RouteDefinition) => {
        this.title = routeDef.title;
        if (routeDef.children) {
          this.isSubNavigationOpen$.next(true);
          return routeDef.children;
        } else {
          this.isSubNavigationOpen$.next(false);
          return [];
        }
      })
    );
  }

  public changeEstablishment(establishment: Establishment): void {
    this.expandedEstablishmentDropdown = false;
    this.establishmentService.changeEstablishment(establishment);
    this.selectedEstablishment = establishment;
    this.routeType = this.getRouteTypeFromPermissionLevel();
  }

  public logout(): void {
    this.authenticationService.logout();
  }

  public showEstablishments(): boolean {
    return this.establishments && this.establishments.length > 0;
  }

  public openSubNavigation(): void {
    this.childRoutes$
      .pipe(
        takeUntil(this.ngUnsubscribe),
        tap((children: Array<RouteDefinition>) =>
          this.isSubNavigationOpen$.next(children.length > 0)
        )
      )
      .subscribe();
  }

  public closeSubNavigation(): void {
    this.isSubNavigationOpen$.next(false);
  }

  public closeSubNavigationOnHover(): void {
    if (!this.isTouchDevice) {
      this.closeSubNavigation();
    }
  }

  public toggleDropdown(establishments: Array<Establishment>): void {
    if (establishments && establishments.length > 1) {
      this.expandedEstablishmentDropdown = !this.expandedEstablishmentDropdown;
    }
  }

  public buildRoute(route: RouteDefinition): string {
    return route.parentPath + '/' + (route.path ? route.path : '');
  }

  public stopEventPropagation(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
  }

  public toggleHelp(): void {
    this.intercom.toggle();
  }

  public canSpin(): boolean {
    return this.router.url !== `/${RoutePaths.NOT_FOUND}`;
  }

  private getRouteTypeFromPermissionLevel(): RouteType {
    if (this.permission.isEmployee()) {
      return RouteType.WAITER;
    } else {
      return RouteType.NORMAL;
    }
  }

  private getOutdatedDevices(): void {
    this.deviceService.getOutdatedDevices().subscribe((count: number) => {
      this.outDatedDevicesCount = count;
    });
  }

  private init(): void {
    this.getOutdatedDevices();

    this.displayNameLoggedinUser$ = this.userService.user.pipe(
      map((user: User) => {
        return user.email || user.username;
      })
    );

    this.productApi.addCategoryMenu$.subscribe(
      (layouts: Array<ProductLayout>) => {
        this.layouts = layouts;
      }
    );

    this.routes$ = this.navigationService.getNavigationMenu();
    this.routes$.subscribe(() => {
      if (this.activatedRoute.snapshot.root.children.length) {
        let route = this.activatedRoute.snapshot.root.children[0];
        let config = route.routeConfig;
        while (!config.path) {
          route = route.children[0];
          config = route.routeConfig;
        }
        if (route.children.length) {
          this.childRoutes$ = this.getChildrenOfRoute(<DynamicRoute>config);
        }
      }
    });
  }

  handleAccountIconClick(event): void {
    event.stopPropagation();

    this.featureFlagService.isNewAccountEnabled().subscribe(isEnabled => {
      if (isEnabled) {
        this.router.navigate([RoutePaths.NEW_ACCOUNT]);
      } else {
        this.router.navigate([RoutePaths.ACCOUNT]);
      }
    });
  }
}
